import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { S } from '../styles.js';

const API_BASE = import.meta.env.VITE_API_URL || '';
const API = `${API_BASE}/api/candidate`;
const QUESTION_TIME = 60; // 1 minute per question

export default function CandidateInterview() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [screen, setScreen] = useState('loading');
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lastEval, setLastEval] = useState(null);
  const [timeLeft, setTimeLeft] = useState(QUESTION_TIME);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const timerRef = useRef(null);
  const submitRef = useRef(null);

  const [listening, setListening] = useState(false);
  const [browserSupportsSpeechRecognition, setBrowserSupportsSpeechRecognition] = useState(true);
  const recognitionRef = useRef(null);
  const screenRef = useRef(screen);
  const submittingRef = useRef(submitting);
  const isVoiceActiveRef = useRef(true);

  // Sync refs for event handlers
  useEffect(() => { screenRef.current = screen; }, [screen]);
  useEffect(() => { submittingRef.current = submitting; }, [submitting]);

  // Initialize native Speech Recognition
  useEffect(() => {
    const SpeechClass = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechClass) {
      setBrowserSupportsSpeechRecognition(false);
      return;
    }

    const rec = new SpeechClass();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = 'en-US';

    rec.onstart = () => {
      setListening(true);
    };

    rec.onend = () => {
      setListening(false);
      if (screenRef.current === 'interview' && !submittingRef.current && isVoiceActiveRef.current) {
        try {
          rec.start();
        } catch (err) {
          console.warn('Speech recognition auto-restart failed:', err);
        }
      }
    };

    rec.onerror = (e) => {
      console.warn('Speech recognition error:', e.error);
      if (e.error === 'not-allowed') {
        setError('Microphone permission blocked or denied.');
      }
    };

    rec.onresult = (e) => {
      const resultIndex = e.resultIndex;
      const resultText = e.results[resultIndex][0].transcript;
      if (resultText) {
        setAnswer((prev) => {
          const base = prev.trim();
          return base ? `${base} ${resultText.trim()}` : resultText.trim();
        });
      }
    };

    recognitionRef.current = rec;

    return () => {
      rec.onend = null;
      rec.stop();
    };
  }, []);

  // Manage start/stop of speech recognition when screen or question changes
  useEffect(() => {
    if (screen === 'interview' && !submitting) {
      setAnswer('');
      isVoiceActiveRef.current = true;
      if (recognitionRef.current && !listening) {
        try {
          recognitionRef.current.start();
        } catch (err) {
          console.warn('Speech recognition start failed:', err);
        }
      }
    }
  }, [session?.currentQuestion?.id, screen, submitting]);

  useEffect(() => { loadSession(); }, [token]);

  // Request webcam & start video recording stream on interview start
  useEffect(() => {
    if (screen !== 'interview') return;
    let streamInstance = null;

    async function initWebcam() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        streamInstance = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
        recordedChunksRef.current = [];
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) recordedChunksRef.current.push(e.data);
        };
        recorder.start(1000);
        mediaRecorderRef.current = recorder;
      } catch (e) {
        console.warn('Webcam permission denied or unhandled:', e);
      }
    }

    initWebcam();

    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (streamInstance) {
        streamInstance.getTracks().forEach((t) => t.stop());
      }
    };
  }, [screen]);

  async function loadSession() {
    try {
      const r = await fetch(`${API}/interview/${token}`);
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to load interview');
      setSession(data);
      setScreen(data.status === 'completed' ? 'completed' : 'welcome');
    } catch (err) {
      setError(err.message);
      setScreen('error');
    }
  }

  // keep a ref so the timer callback always calls the latest version
  const submitAnswer = useCallback(async (forcedText) => {
    const text = typeof forcedText === 'string' ? forcedText : answer;
    if (!text.trim()) { setError('Please provide an answer before submitting.'); return; }
    clearTimer();
    isVoiceActiveRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setSubmitting(true); setError(''); setLastEval(null);
    try {
      const r = await fetch(`${API}/interview/${token}/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answerText: text }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Submission failed');
      setLastEval(data.evaluation);
      setAnswer('');
      if (data.completed) {
        // Upload recorded video
        if (recordedChunksRef.current.length > 0) {
          const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
          const formData = new FormData();
          formData.append('video', blob, `interview-${token}.webm`);
          fetch(`${API}/interview/${token}/upload-video`, {
            method: 'POST',
            body: formData,
          }).catch((err) => console.error('Video upload error:', err));
        }

        setSession((prev) => ({ ...prev, status: 'completed', report: data.report }));
        setScreen('completed');
      } else {
        setTimeLeft(QUESTION_TIME);
        setSession((prev) => ({
          ...prev,
          currentQuestion: data.nextQuestion,
          progress: data.progress,
          answeredCount: data.answeredCount,
          totalQuestions: data.totalQuestions,
        }));
      }
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  }, [answer, token]);

  // keep ref in sync so timer closure always has latest submitAnswer
  useEffect(() => { submitRef.current = submitAnswer; }, [submitAnswer]);

  function clearTimer() {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }

  // start / restart countdown whenever question changes or screen becomes 'interview'
  useEffect(() => {
    if (screen !== 'interview' || submitting) return;
    clearTimer();
    setTimeLeft(QUESTION_TIME);
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          submitRef.current('[No response — time expired]');
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return clearTimer;
  }, [screen, session?.currentQuestion?.id, submitting]);

  function toggleVoice() {
    if (submitting) return;
    setError('');
    if (!browserSupportsSpeechRecognition) {
      setError('Voice input is not supported in this browser. Please use Google Chrome or Microsoft Edge.');
      return;
    }

    if (listening) {
      isVoiceActiveRef.current = false;
      recognitionRef.current?.stop();
    } else {
      isVoiceActiveRef.current = true;
      try {
        recognitionRef.current?.start();
      } catch (err) {
        console.warn('Speech recognition start failed:', err);
      }
    }
  }

  const pct = session ? Math.round(((session.answeredCount || 0) / (session.totalQuestions || 1)) * 100) : 0;
  const timerPct = (timeLeft / QUESTION_TIME) * 100;
  const timerColor = timeLeft > 15 ? '#4ade80' : timeLeft > 8 ? '#fbbf24' : '#f87171';
  const timerUrgent = timeLeft <= 10;

  // ── Loading ──
  if (screen === 'loading') return (
    <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', margin: '0 auto 16px', border: '3px solid rgba(79,209,255,0.15)', borderTopColor: '#4fd1ff', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ color: '#4fd1ff', fontSize: 15, fontWeight: 600 }}>Loading your interview…</p>
      </div>
    </div>
  );

  // ── Error ──
  if (screen === 'error') return (
    <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <div style={{ ...S.card, maxWidth: 440, textAlign: 'center', padding: '44px 32px' }}>
        <p style={{ fontSize: 48, margin: '0 0 16px' }}>🔗</p>
        <h2 style={{ margin: '0 0 10px', color: '#f87171', fontSize: 20 }}>Interview Not Found</h2>
        <p style={{ color: '#6b8aad', lineHeight: 1.7, fontSize: 14 }}>{error}</p>
      </div>
    </div>
  );

  // ── Welcome / Intro ──
  if (screen === 'welcome') return (
    <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f8fafc' }}>
      <div style={{ maxWidth: 580, width: '100%', display: 'grid', gap: 16 }} className="fade-in">

        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 6 }}>
          <div style={{
            width: 60, height: 60, borderRadius: 18,
            background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 28, color: '#ffffff', margin: '0 auto 12px',
            boxShadow: '0 8px 24px rgba(37,99,235,0.25)'
          }}>🤖</div>
          <p style={{ margin: 0, fontSize: 11, color: '#2563eb', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase' }}>AI Interview Portal</p>
        </div>

        {/* Welcome card */}
        <div style={{ ...S.card, padding: '32px' }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 24, fontWeight: 800, color: '#0f172a' }}>
            Welcome{session?.candidateName ? `, ${session.candidateName}` : ''}!
          </h2>
          <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: 14 }}>
            You're interviewing for <span style={{ color: '#2563eb', fontWeight: 700 }}>{session?.jobTitle}</span>
          </p>

          {/* What to expect */}
          <p style={{ margin: '0 0 14px', fontSize: 11, color: '#2563eb', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>What to expect</p>
          <div style={{ display: 'grid', gap: 12, marginBottom: 28 }}>
            {[
              { icon: '❓', title: `${session?.totalQuestions || 'Several'} technical questions`, desc: 'Tailored specifically to your experience and JD requirements' },
              { icon: '🎤', title: 'Real-time voice & video', desc: 'Speak naturally — your answer is transcribed automatically' },
              { icon: '🤖', title: 'AI evaluation engine', desc: 'Responses evaluated confidentiality for recruiter review' },
            ].map((item) => (
              <div key={item.title} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', background: '#f8fafc', padding: '12px 16px', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{item.icon}</div>
                <div>
                  <p style={{ margin: '0 0 2px', fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{item.title}</p>
                  <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={{ height: 1, background: '#e2e8f0', marginBottom: 24 }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>⏱ Allocated time: <strong style={{color:'#0f172a'}}>1 minute</strong> per question.</p>
            <button onClick={() => setScreen('interview')} style={{
              padding: '12px 24px', borderRadius: 12, border: 'none',
              background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)',
              color: '#ffffff', fontWeight: 800, fontSize: 14, cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(37,99,235,0.25)'
            }}>
              Start Interview →
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Completed ──
  if (screen === 'completed') {
    return (
      <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f8fafc' }}>
        <div style={{ maxWidth: 540, width: '100%', padding: '40px 28px', textAlign: 'center' }} className="fade-in">
          <div style={S.card}>
            <div style={{
              width: 76, height: 76, borderRadius: '50%', margin: '0 auto 20px',
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 38,
            }}>🎉</div>

            <h2 style={{ margin: '0 0 10px', fontSize: 26, fontWeight: 800, color: '#0f172a' }}>
              Thank You{session?.candidateName ? `, ${session.candidateName}` : ''}!
            </h2>

            <p style={{ margin: '0 0 20px', fontSize: 15, color: '#475569', lineHeight: 1.7 }}>
              Your interview responses and video recording have been submitted successfully.
            </p>

            <div style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: 14,
              padding: '18px 20px',
              marginBottom: 24,
            }}>
              <p style={{ margin: 0, fontSize: 14, color: '#1d4ed8', fontWeight: 600, lineHeight: 1.6 }}>
                🤝 We appreciate your time! Our recruiting team will review your responses and we will connect with you soon.
              </p>
            </div>

            <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>
              You may now safely close this browser window.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Interview ──
  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', paddingBottom: 60 }}>

      {/* Stitch Top Header Navbar */}
      <header style={{
        background: '#ffffff', borderBottom: '1px solid #e2e8f0',
        padding: '16px 36px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 32, boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: 'linear-gradient(135deg,#2563eb,#1d4ed8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, color: '#ffffff', fontWeight: 800
          }}>🤖</div>
          <span style={{ fontSize: 16, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em' }}>
            AI INTERVIEW PORTAL
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div style={{ display: 'flex', gap: 16, fontSize: 13, fontWeight: 600, color: '#64748b' }}>
            <span style={{ color: '#2563eb', fontWeight: 700 }}>Interviews</span>
            <span>History</span>
            <span>Support</span>
          </div>
          <div style={{
            width: 34, height: 34, borderRadius: '50%', background: '#2563eb',
            color: '#ffffff', fontWeight: 800, fontSize: 13,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            {session?.candidateName ? session.candidateName.charAt(0).toUpperCase() : 'C'}
          </div>
        </div>
      </header>

      {/* Main Content Container */}
      <div style={{ maxWidth: 1040, margin: '0 auto', padding: '0 20px', display: 'grid', gap: 20 }}>

        {/* Progress Header Pill */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{
            fontSize: 12, fontWeight: 800, color: '#2563eb',
            background: '#eff6ff', border: '1px solid #bfdbfe',
            padding: '5px 14px', borderRadius: 8, textTransform: 'uppercase', letterSpacing: '0.06em'
          }}>
            QUESTION {session?.progress || '1 OF 11'}
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>
            🔴 REC Recording
          </span>
        </div>

        {/* Main 2-Column Split Professional Layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>

          {/* Left Column: HD Professional Studio Webcam Container */}
          <div style={{
            background: '#ffffff', borderRadius: 24, padding: '20px',
            border: '1px solid #e2e8f0', boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
            position: 'sticky', top: 20
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', animation: 'pulse 1.2s ease infinite' }} />
                <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  STUDIO CAMERA PREVIEW
                </span>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '2px 8px', borderRadius: 6 }}>
                1080p HD
              </span>
            </div>

            <div style={{
              position: 'relative', width: '100%', aspectRatio: '4 / 3',
              borderRadius: 16, overflow: 'hidden', background: '#0f172a',
              border: '1px solid #cbd5e1', boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%', height: '100%', objectFit: 'cover',
                  transform: 'scaleX(-1)', display: 'block'
                }}
              />
              <div style={{
                position: 'absolute', bottom: 12, left: 12, right: 12,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)',
                padding: '6px 12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.15)'
              }}>
                <span style={{ fontSize: 11, color: '#ffffff', fontWeight: 700 }}>
                  {listening ? '🎙️ Mic Active (Speaking…)' : '🎤 Mic Ready'}
                </span>
                <span style={{ fontSize: 11, color: '#93c5fd', fontWeight: 600 }}>
                  Auto-Transcribe ON
                </span>
              </div>
            </div>

            <p style={{ margin: '14px 0 0', fontSize: 12, color: '#64748b', textAlign: 'center' }}>
              Ensure your face is clearly lit & visible within the camera frame.
            </p>
          </div>

          {/* Right Column: Question & Answer Panel */}
          <div style={{ display: 'grid', gap: 20 }}>
            
            {/* Question card */}
            <div className="fade-in" style={{
              background: '#ffffff', borderRadius: 24, padding: '28px',
              border: '1px solid #e2e8f0', boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
            }}>

              {/* Tags */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                {session?.currentQuestion?.skill && !/domain expertise|domain experience|operations|professional competencies|role competencies|core skills/i.test(session.currentQuestion.skill) && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', background: '#eff6ff', padding: '3px 10px', borderRadius: 6, border: '1px solid #bfdbfe' }}>
                    {session.currentQuestion.skill}
                  </span>
                )}
                <span style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', background: '#f5f3ff', padding: '3px 10px', borderRadius: 6, border: '1px solid #ddd6fe' }}>
                  {session?.currentQuestion?.type || 'Technical'}
                </span>
              </div>

              {/* Question Text Box */}
              <div style={{
                padding: '20px 24px', borderRadius: 16, background: '#eff6ff',
                border: '1px solid #bfdbfe', marginBottom: 20, position: 'relative'
              }}>
                <p style={{ margin: 0, fontSize: 16, lineHeight: 1.7, color: '#0f172a', fontWeight: 800 }}>
                  {session?.currentQuestion?.question}
                </p>
              </div>

              {/* Answer input */}
              <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#2563eb', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.06em' }}>
                YOUR ANSWER (Real-Time Audio Transcribed)
              </label>
              {/* Answer input container with inside Mic indicator */}
              <div style={{ position: 'relative', width: '100%' }}>
                <textarea
                  value={answer}
                  onChange={(e) => {
                    setAnswer(e.target.value);
                    if (listening) {
                      isVoiceActiveRef.current = false;
                      recognitionRef.current?.stop();
                    }
                  }}
                  style={{
                    width: '100%', padding: '16px 54px 16px 16px', borderRadius: 16,
                    border: listening ? '2px solid #2563eb' : '1px solid #cbd5e1',
                    background: '#ffffff', color: '#0f172a',
                    fontSize: 14, fontWeight: 500, lineHeight: 1.6, minHeight: 150, outline: 'none',
                    boxShadow: listening ? '0 0 0 4px rgba(37,99,235,0.1)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                  placeholder="Type your answer here, or speak naturally into microphone for auto-transcription…"
                />
                
                {/* Floating Mic Button Inside Box */}
                <button
                  onClick={toggleVoice}
                  disabled={submitting}
                  title={listening ? 'Microphone Active — Click to pause' : 'Click to activate microphone'}
                  style={{
                    position: 'absolute', top: 14, right: 14,
                    width: 38, height: 38, borderRadius: '50%',
                    border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: listening ? '#2563eb' : '#f1f5f9',
                    color: listening ? '#ffffff' : '#64748b',
                    boxShadow: listening ? '0 4px 14px rgba(37,99,235,0.35)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                  </svg>
                </button>

                {/* Live Mic Status Badge */}
                {listening && (
                  <div style={{
                    position: 'absolute', bottom: 14, right: 16,
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: 'rgba(37,99,235,0.08)', border: '1px solid #bfdbfe',
                    padding: '3px 10px', borderRadius: 20
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#2563eb', animation: 'pulse 1s ease infinite' }} />
                    <span style={{ fontSize: 10, fontWeight: 800, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Mic Listening
                    </span>
                  </div>
                )}
              </div>

              {error && <div style={S.error}><span>⚠</span> {error}</div>}

              {/* 1-Minute Warning Banner */}
              {timerUrgent && !submitting && (
                <div style={{
                  marginTop: 14, padding: '10px 16px', borderRadius: 10,
                  background: '#fef2f2', border: '1px solid #fecaca',
                  display: 'flex', alignItems: 'center', gap: 10,
                  animation: 'pulse 1.2s ease infinite',
                }}>
                  <span style={{ fontSize: 16 }}>⚠️</span>
                  <span style={{ fontSize: 13, color: '#dc2626', fontWeight: 700 }}>
                    1 minute warning: Please submit answer ({timeLeft}s left)
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                    {listening ? '🎙️ Speech Engine Active' : '🎤 Click mic icon inside text area to start'}
                  </span>
                </div>
                <button
                  onClick={() => submitAnswer()}
                  disabled={submitting}
                  style={{
                    padding: '12px 24px', borderRadius: 12, border: 'none',
                    background: '#2563eb', color: '#ffffff', fontWeight: 800, fontSize: 14, cursor: 'pointer',
                    boxShadow: '0 4px 14px rgba(37,99,235,0.25)'
                  }}
                >
                  {submitting ? 'Evaluating…' : 'Submit Answer →'}
                </button>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}

function TimerRing({ timeLeft, pct, color, urgent }) {
  const size = 52;
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * (pct / 100);
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={5} />
        <circle
          cx={size/2} cy={size/2} r={r} fill="none"
          stroke={color} strokeWidth={5}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dasharray 0.9s linear, stroke 0.4s',
            filter: urgent ? `drop-shadow(0 0 5px ${color})` : 'none',
          }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column',
      }}>
        <span style={{
          fontSize: 14, fontWeight: 800, color,
          animation: urgent ? 'pulse 1s ease infinite' : 'none',
          lineHeight: 1,
        }}>{timeLeft}</span>
        <span style={{ fontSize: 9, color: '#6b8aad', fontWeight: 600, letterSpacing: '0.04em' }}>SEC</span>
      </div>
    </div>
  );
}

